/**
 * Image-edit pipeline for photoclear.
 *
 * Routed through Google Gemini (`gemini-3.1-flash-image`) via the
 * `gemini-image` Supabase Edge Function so the Gemini API key stays
 * server-side instead of being baked into the public JS bundle.
 *
 * History:
 *   - Originally called Gemini directly from the browser using
 *     VITE_API_KEY. The bundled key got suspended.
 *   - Briefly proxied through `replicate-nano-banana` (Replicate's
 *     `google/nano-banana-2`) — kept the key safe but the model was
 *     unreliable for our prompts.
 *   - Now back on Gemini, but server-side via the `gemini-image` edge
 *     function (set `GEMINI_API_KEY` as a Supabase secret).
 *
 * The exported function signatures are preserved exactly so the rest of
 * the app (App.tsx, prompts in constants.ts, etc.) is unchanged.
 */

const SUPABASE_URL = process.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.VITE_SUPABASE_ANON_KEY || '';
const FN_URL = `${SUPABASE_URL}/functions/v1/gemini-image`;
export const PHOTOCLEAR_IMAGE_MODEL = 'gemini-3.1-flash-image';

const MAX_RETRIES = 3;
const INITIAL_DELAY_MS = 2000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const isRetryableError = (msg: string): boolean =>
  /429|rate.?limit|quota|RESOURCE_EXHAUSTED|temporarily unavailable|capacity|timed?[- ]out|interrupted|\(code:\s*PA\)|502|503|504|546|WORKER_LIMIT|resource limit/i.test(
    msg,
  );

/**
 * Fetch a hosted image URL and convert it to a base64 data URL so the
 * caller (App.tsx) can keep treating outputs as inline data, matching
 * the previous Gemini contract.
 */
async function urlToDataUrl(url: string): Promise<string> {
  if (url.startsWith('data:')) return url;
  const r = await fetch(url);
  if (!r.ok) throw new Error(`Failed to fetch generated image: ${r.status}`);
  const blob = await r.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read generated image as data URL'));
    reader.readAsDataURL(blob);
  });
}

// Gemini accepts: 1:1, 3:4, 4:3, 9:16, 16:9. Anything else (e.g. 21:9
// used by padded 360 panoramas elsewhere, or "match_input_image") is
// passed through as `match_input_image`; the edge function drops it
// from the imageConfig so Gemini infers the ratio from the input.
function normaliseAspect(a?: string): string {
  if (!a) return 'match_input_image';
  const allowed = new Set(['1:1', '3:4', '4:3', '9:16', '16:9']);
  return allowed.has(a) ? a : 'match_input_image';
}

interface RunOpts {
  prompt: string;
  images: string[]; // data: URLs or https URLs, up to 14
  aspectRatio?: string;
}

async function runGemini(opts: RunOpts): Promise<string> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error(
      'Image service not configured: VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY missing.',
    );
  }

  let lastError: any;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      if (attempt > 0) {
        const delay = INITIAL_DELAY_MS * Math.pow(2, attempt - 1);
        console.log(`Gemini retry ${attempt + 1}/${MAX_RETRIES} after ${delay}ms`);
        await sleep(delay);
      }

      const r = await fetch(FN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          prompt: opts.prompt,
          images: opts.images,
          aspect_ratio: normaliseAspect(opts.aspectRatio),
          resolution: '4K',
          output_format: 'png',
          model: PHOTOCLEAR_IMAGE_MODEL,
        }),
      });

      const data = await r.json().catch(() => ({}) as any);
      if (!r.ok || !data?.imageUrl) {
        throw new Error(data?.error || `Edge function error ${r.status}`);
      }

      return await urlToDataUrl(data.imageUrl);
    } catch (e: any) {
      lastError = e;
      const msg = e?.message || String(e);
      console.error(`Gemini attempt ${attempt + 1} failed:`, msg);
      if (!isRetryableError(msg) || attempt === MAX_RETRIES - 1) throw e;
    }
  }
  throw lastError;
}

/**
 * Sends the image (with the burnt-in red mask) to the model for editing.
 * Maintains the original framing/perspective via prompt guardrails.
 */
export const declutterImage = async (
  imageBase64: string,
  promptText: string,
  aspectRatio: string = '1:1',
): Promise<string> => {
  const fullPrompt =
    `${promptText} ` +
    `STRICT REQUIREMENT: Maintain the exact framing, perspective, lighting, and ` +
    `camera angle of the original image. Do not crop, zoom, or distort the room ` +
    `geometry. Perform the requested changes naturally while preserving the ` +
    `structural integrity of the room. ` +
    `Any red overlay or red brush marks in the input image are a control signal ` +
    `marking the area to change — do NOT carry the red colour into the output; ` +
    `replace those pixels with the requested edit.`;

  return runGemini({
    prompt: fullPrompt,
    images: [imageBase64],
    aspectRatio,
  });
};

/**
 * Upscales the provided image to 4K. Gemini is asked to keep
 * composition and content identical and only improve fidelity.
 */
export const upscaleImage = async (imageBase64: string): Promise<string> => {
  return runGemini({
    prompt:
      'Upscale this image to high quality 4K resolution. Improve sharpness, ' +
      'lighting, and texture details while maintaining the original composition ' +
      'and content exactly. Do not add, remove, or rearrange any objects. ' +
      'Do not change the colour balance.',
    images: [imageBase64],
    aspectRatio: 'match_input_image',
  });
};
